import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Label, Spinner, TextInput } from "flowbite-react";
import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { Check } from "@phosphor-icons/react";
import AnimatedModal from "../../../../components/AnimatedModal";
import { useAuth } from "../../../../hooks";
import Select from "react-select";
import { useRoles } from "../../../../hooks/get/useRoles";
import {
  customSelectStyles,
  PERMISSION_MAPPER,
} from "../../../../shared/constants";
import { useTheme } from "../../../../providers/ThemeProvider";
import { InviteTeamMemberModalProps } from "../interface";
import { checkPermission } from "../../../../lib/utils";

interface FormData {
  email: string;
  roleId: number;
}

// Define the type for role options
interface RoleOptionType {
  value: number;
  label: string;
  description: string;
}

const InviteTeamMemberModal: React.FC<InviteTeamMemberModalProps> = ({
  open,
  onClose,
  onInvite,
  selectedMember,
  isInviting,
  existingMembers = [],
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { data: roles = [], isLoading: isLoadingRoles } = useRoles();

  const roleOptions: RoleOptionType[] = useMemo(
    () =>
      roles.map((role) => ({
        value: role.id,
        label: role.name,
        description: role.description,
      })),
    [roles]
  );

  const isEditMode = !!selectedMember;

  const schema = yup.object().shape({
    email: yup
      .string()
      .required(t("configurations.team.emailRequired"))
      .email(t("configurations.team.invalidEmail"))
      .test(
        "not-self",
        t("configurations.team.cannotInviteYourself"),
        (value) => {
          if (!value || !user?.email) return true;
          return value.toLowerCase() !== user.email.toLowerCase();
        }
      )
      .test(
        "not-existing",
        t("configurations.team.emailAlreadyInvited"),
        (value) => {
          if (!value) return true;
          const members = Array.isArray(existingMembers) ? existingMembers : [];
          return !members.some(
            (member) =>
              member &&
              member.email &&
              member.email.toLowerCase() === value.toLowerCase() &&
              (!selectedMember ||
                (selectedMember.email &&
                  member.email.toLowerCase() !==
                    selectedMember.email.toLowerCase()))
          );
        }
      ),
    roleId: yup.number().required(t("configurations.roles.selectRole")),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver<FormData>(schema),
    defaultValues: {
      email: "",
      roleId: -1,
    },
    mode: "onChange",
  });

  // Reset form when modal opens/closes or when selectedMember changes
  useEffect(() => {
    if (open) {
      if (isEditMode && selectedMember && roles.length) {
        const foundRole = roles.find(
          (role) => role.id === selectedMember.role?.id
        );
        reset({
          email: selectedMember.user?.email || selectedMember.email || "",
          roleId: foundRole?.id || -1,
        });
      } else {
        reset({
          email: "",
          roleId: roleOptions[0]?.value || -1,
        });
      }
    }
  }, [open, selectedMember, isEditMode, roles, roleOptions, reset]);

  const onSubmit = (data: FormData) => {
    onInvite(data.email, data.roleId);
  };

  const hasRoleReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.ROLE_MANAGEMENT_READ,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  return (
    <AnimatedModal
      show={open}
      onClose={onClose}
      title={
        isEditMode
          ? t("configurations.team.edit")
          : t("configurations.team.invite")
      }
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="email" value={t("configurations.team.email")} />
          <TextInput
            id="email"
            type="email"
            {...register("email")}
            disabled={isInviting || isEditMode}
            placeholder="name@company.com"
            color={errors.email ? "failure" : undefined}
            helperText={errors.email?.message}
          />
        </div>

        {hasRoleReadPermission && (
          <div>
            <Label htmlFor="role" value={t("configurations.roles.role")} />
            <Controller
              name="roleId"
              control={control}
              render={({ field }) => (
                <Select<RoleOptionType>
                  id="role"
                  options={roleOptions}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base, state) => ({
                      ...base,
                      minHeight: "44px",
                      backgroundColor: isDark ? "#3a3a3a" : "#ffffff",
                      borderColor: state.isFocused
                        ? isDark ? "#737373" : "#9ca3af"
                        : isDark ? "#525252" : "#e5e7eb",
                      boxShadow: state.isFocused
                        ? isDark ? "0 0 0 3px rgba(255,255,255,0.06)" : "0 0 0 3px rgba(0,0,0,0.06)"
                        : "none",
                      borderRadius: "10px",
                      cursor: "pointer",
                      transition: "border-color 0.15s, box-shadow 0.15s",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0 12px",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: isDark ? "#f3f4f6" : "#111827",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      margin: 0,
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: isDark ? "#737373" : "#9ca3af",
                      fontSize: "0.875rem",
                    }),
                    indicatorSeparator: () => ({ display: "none" }),
                    dropdownIndicator: (base, state) => ({
                      ...base,
                      color: isDark ? "#737373" : "#9ca3af",
                      paddingRight: "10px",
                      transition: "transform 0.2s",
                      transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: "12px",
                      overflow: "hidden",
                      padding: "6px",
                      backgroundColor: isDark ? "#262626" : "#ffffff",
                      border: `1px solid ${isDark ? "#404040" : "#e5e7eb"}`,
                      boxShadow: isDark
                        ? "0 16px 40px rgba(0,0,0,0.6)"
                        : "0 16px 40px rgba(0,0,0,0.14)",
                    }),
                    menuList: (base) => ({
                      ...base,
                      padding: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }),
                    option: (base, state) => ({
                      ...base,
                      padding: "10px 12px",
                      backgroundColor: state.isSelected
                        ? isDark ? "#3a3a3a" : "rgba(0,0,0,0.06)"
                        : state.isFocused
                        ? isDark ? "#303030" : "rgba(0,0,0,0.04)"
                        : isDark ? "#262626" : "transparent",
                      color: isDark ? "#f3f4f6" : "#111827",
                      cursor: "pointer",
                      borderRadius: "8px",
                      border: state.isSelected
                        ? `1px solid ${isDark ? "#525252" : "rgba(0,0,0,0.08)"}`
                        : "1px solid transparent",
                      transition: "background-color 0.1s",
                    }),
                  }}
                  value={
                    roleOptions.find(
                      (option) => option.value === field.value
                    ) || null
                  }
                  onChange={(option) => {
                    field.onChange(option?.value || -1);
                  }}
                  formatOptionLabel={(option, { context, selectValue }) => {
                    const isSelected = selectValue.some((v) => v.value === option.value);
                    if (context === "value") {
                      return (
                        <span className="font-medium text-sm">{option.label}</span>
                      );
                    }
                    return (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex flex-col gap-[3px] min-w-0">
                          <span className="font-semibold text-[13px] leading-tight truncate">{option.label}</span>
                          {option.description && option.description !== "-" && (
                            <span
                              className="text-[11px] leading-snug truncate"
                              style={{ color: isDark ? "rgba(156,163,175,0.9)" : "rgba(107,114,128,0.9)" }}
                            >
                              {option.description}
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <Check
                            size={14}
                            weight="bold"
                            className="flex-shrink-0"
                            style={{ color: isDark ? "#9ca3af" : "#6b7280" }}
                          />
                        )}
                      </div>
                    );
                  }}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isSearchable={false}
                  classNamePrefix="custom-select"
                  isDisabled={isInviting || isLoadingRoles}
                  isLoading={isLoadingRoles}
                  placeholder={t("configurations.roles.selectRole")}
                />
              )}
            />
            {errors.roleId && (
              <span className="text-xs text-red-500">
                {errors.roleId.message}
              </span>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-2 mt-6">
          <Button color="light" onClick={onClose} disabled={isInviting}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={isInviting || Object.keys(errors).length > 0}
            color={"indigo"}
          >
            <div className="flex items-center gap-2">
              {isInviting && <Spinner color={"purple"} size={"sm"} />}
              {isEditMode
                ? t("common.update")
                : t("configurations.team.invite")}
            </div>
          </Button>
        </div>
      </form>
    </AnimatedModal>
  );
};

export default InviteTeamMemberModal;
